import "./styles.css";
import { StreamVideoClient, Call, CallingState } from "@stream-io/video-client";
import { cleanupParticipant, renderParticipant } from "./participant";
import { renderControls } from "./controls";
import {
  renderAudioDeviceSelector,
  renderAudioOutputSelector,
  renderVideoDeviceSelector,
} from "./device-selector";
import { isMobile } from "./mobile";
import { ChatService } from "./chat-service";
import { ChatUI } from "./chat-ui";
import { ReviewUI } from "./review-ui";
import {
  captureMonitoringException,
  captureMonitoringMessage,
  initMonitoring,
  setMonitoringContext,
  setMonitoringUser,
} from "./sentry";

const API_URL = process.env.API_URL || "https://api.saludindependiente.com/v1";
const DEFAULT_DISCONNECTION_TIMEOUT_SECONDS = 30;
const DISCONNECTION_TIMEOUT_SECONDS = Number.parseInt(
  process.env.STREAM_DISCONNECTION_TIMEOUT_SECONDS ||
    `${DEFAULT_DISCONNECTION_TIMEOUT_SECONDS}`,
  10,
);

const appContainer = document.getElementById("app-container")!;
const speakerView = document.getElementById("speaker-view")!;
const sidebarParticipants = document.getElementById("sidebar-participants")!;
const controlsContainer = document.getElementById("controls-container")!;
const callControls = document.getElementById("call-controls")!;
const mainView = document.getElementById("main-view")!;

const createOverlay = (content: string, id: string) => {
  const div = document.createElement("div");
  div.id = id;
  div.className = "full-screen-overlay";
  div.innerHTML = content;
  document.body.appendChild(div);
  return div;
};

const loadingScreen = createOverlay(
  `
  <div class="spinner"></div>
  <div class="loading-text">Conectandose al turno...</div>
`,
  "loading-screen",
);

const errorScreen = createOverlay("", "error-screen");
errorScreen.classList.add("hidden");

const connectionStatusBanner = document.createElement("div");
connectionStatusBanner.id = "connection-status-banner";
connectionStatusBanner.className = "connection-status-banner hidden";
connectionStatusBanner.setAttribute("role", "status");
connectionStatusBanner.setAttribute("aria-live", "polite");
document.body.appendChild(connectionStatusBanner);

const thankYouScreen = createOverlay(
  `
  <div class="thank-you-container">
    <div class="thank-you-title">Consulta finalizada</div>
    <div class="thank-you-desc">Ahora puede cerrar esta pestana.</div>
  </div>
`,
  "thank-you-screen",
);
thankYouScreen.classList.add("hidden");

const welcomeScreen = createOverlay(
  `
  <div class="welcome-container">
    <div class="welcome-title">Bienvenido a SI Telemedicina</div>
    <div class="welcome-desc">Para comenzar, abra el enlace de la consulta.</div>
  </div>
`,
  "welcome-screen",
);
welcomeScreen.classList.add("hidden");

const showError = (message: string) => {
  loadingScreen.classList.add("hidden");
  errorScreen.innerHTML = `
    <div class="error-container">
      <div class="error-title">No se pudo unir</div>
      <div class="error-desc">${message}</div>
      <button onclick="window.location.reload()" style="padding: 8px 16px; border-radius: 4px; border:none; background:#444; color:white; cursor:pointer;">Intentar de nuevo</button>
    </div>
  `;
  errorScreen.classList.remove("hidden");
};

const createWaitingState = (participantRole: string) => {
  const waitingState = document.createElement("div");
  waitingState.id = "waiting-state";
  waitingState.className = "waiting-state hidden";

  const isDoctor = participantRole === "doctor";
  const title = isDoctor ? "Esperando al paciente" : "Esperando al profesional";
  const description = isDoctor
    ? "La consulta comenzara cuando el paciente se una a la videollamada."
    : "La consulta comenzara cuando el profesional se una a la videollamada.";

  waitingState.innerHTML = `
    <div class="waiting-card">
      <div class="waiting-badge">Sala de espera</div>
      <div class="waiting-title">${title}</div>
      <div class="waiting-desc">${description}</div>
    </div>
  `;

  speakerView.appendChild(waitingState);
  return waitingState;
};

const navigateBackOrShowThankYou = () => {
  if (window.history.length > 1) {
    window.history.back();
    return;
  }

  appContainer.classList.add("hidden");
  thankYouScreen.classList.remove("hidden");
};

async function init() {
  initMonitoring();

  try {
    const pathname = window.location.pathname || "";

    const reviewMatch = pathname.match(/\/appointments\/([^\/]+)\/reviews?/);

    if (reviewMatch) {
      const appointmentId = reviewMatch[1];
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");

      if (!token) {
        showError("Token de autenticacion requerido.");
        return;
      }

      loadingScreen.classList.add("hidden");
      new ReviewUI(appointmentId, token, API_URL);
      return;
    }

    if (!pathname.includes("/call")) {
      loadingScreen.classList.add("hidden");
      welcomeScreen.classList.remove("hidden");
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get("token");

    let appointmentId = urlParams.get("appointmentId");
    if (!appointmentId) {
      const pathSegments = window.location.pathname.split("/");
      const callIndex = pathSegments.indexOf("call");
      if (callIndex !== -1 && pathSegments[callIndex + 1]) {
        appointmentId = pathSegments[callIndex + 1];
      }
    }

    if (!tokenParam || !appointmentId) {
      captureMonitoringMessage("Missing call credentials in URL", "warning", {
        pathname,
        hasToken: Boolean(tokenParam),
        hasAppointmentId: Boolean(appointmentId),
      });
      throw new Error("Faltan token o ID de turno.");
    }

    setMonitoringContext({ page: "call", appointmentId });

    const res = await fetch(`${API_URL}/video/token?token=${tokenParam}`, {
      method: "POST",
      body: JSON.stringify({ appointmentId }),
      headers: { "Content-Type": "application/json" },
    });

    if (!res.ok) {
      let errMsg = `Error ${res.status}: ${res.statusText || "Error de conexion al servidor"}`;
      try {
        const errData = await res.json();
        if (errData) {
          if (typeof errData.error === "string") {
            errMsg = errData.error;
          } else if (errData.error?.message) {
            errMsg = errData.error.message;
          } else if (errData.message) {
            errMsg = errData.message;
          }
        }
      } catch {
        // Ignore JSON errors
      }

      captureMonitoringException(new Error(errMsg), {
        feature: "video-token",
        appointmentId,
        status: res.status,
      });
      throw new Error(errMsg);
    }

    const data = await res.json();
    const participantRole = data.participant?.role || "patient";
    setMonitoringContext({ page: "call", appointmentId, participantRole });
    setMonitoringUser({
      id: data.userId,
      username: data.participant?.name,
    });

    const client = new StreamVideoClient({
      apiKey: data.apiKey,
      user: {
        id: data.userId,
        name: data.participant.name,
        image: data.participant.imageUrl,
      },
      token: data.token,
      options: { logLevel: "warn", timeout: 10000 },
    });

    const chatService = new ChatService(
      API_URL,
      appointmentId,
      data.userId,
      tokenParam,
    );
    const chatUI = new ChatUI(chatService, mainView);
    const call = client.call("telehealth", appointmentId);

    setupDisconnectionTimeout(call, appointmentId, participantRole);
    registerMonitoring(client, call, appointmentId, participantRole);

    const handleLeave = async () => {
      const isDoctor = participantRole === "doctor";
      const confirmMessage = isDoctor
        ? "Estas seguro que queres finalizar la consulta para todos?"
        : "Estas seguro que queres salir de la consulta?";

      if (window.confirm(confirmMessage)) {
        try {
          chatService.stopPolling();
          if (isDoctor) {
            await call.endCall();
          } else {
            await call.leave();
          }
        } catch (error) {
          console.error("Error leaving call:", error);
          captureMonitoringException(error, {
            feature: "leave-call",
            appointmentId,
            participantRole,
            action: isDoctor ? "endCall" : "leave",
          });
        } finally {
          navigateBackOrShowThankYou();
        }
      }
    };

    const toggleChat = () => chatUI.toggle();
    setupUI(call, handleLeave, toggleChat);

    await call.join({ create: true });

    try {
      call.camera.enable();
      call.microphone.enable();
    } catch (error) {
      captureMonitoringException(error, {
        feature: "device-enable",
        appointmentId,
      });
    }

    setupLayoutManager(call, participantRole);
    loadingScreen.classList.add("hidden");
  } catch (err: unknown) {
    console.error(err);
    captureMonitoringException(err, { feature: "app-init" });
    const message =
      err instanceof Error ? err.message : "Ocurrio un error desconocido.";
    showError(message);
  }
}

function setupUI(call: Call, onLeave: () => void, onToggleChat: () => boolean) {
  const settingsMenu = document.createElement("div");
  settingsMenu.id = "settings-menu";

  const micLabel = document.createElement("label");
  micLabel.innerText = "Microfono";
  settingsMenu.appendChild(micLabel);
  settingsMenu.appendChild(renderAudioDeviceSelector(call));

  const speakerLabel = document.createElement("label");
  speakerLabel.innerText = "Altavoz";
  settingsMenu.appendChild(speakerLabel);
  const audioOutput = renderAudioOutputSelector(call);
  if (audioOutput) {
    settingsMenu.appendChild(audioOutput);
  }

  if (!isMobile.any()) {
    const camLabel = document.createElement("label");
    camLabel.innerText = "Camara";
    settingsMenu.appendChild(camLabel);
    settingsMenu.appendChild(renderVideoDeviceSelector(call));
  }
  controlsContainer.appendChild(settingsMenu);

  const controls = renderControls(call, onLeave);

  callControls.appendChild(controls.audioButton);
  callControls.appendChild(controls.videoButton);

  if (isMobile.any()) {
    callControls.appendChild(controls.flipButton);
  }

  controls.chatButton.addEventListener("click", () => {
    const isOpen = onToggleChat();
    controls.chatButton.classList.toggle("active", isOpen);
  });
  callControls.appendChild(controls.chatButton);

  controls.settingsButton.addEventListener("click", () => {
    settingsMenu.classList.toggle("open");
    controls.settingsButton.classList.toggle("active");
  });
  callControls.appendChild(controls.settingsButton);

  callControls.appendChild(controls.leaveButton);

  window.addEventListener("beforeunload", () => {
    call.leave();
  });
}

function setupLayoutManager(call: Call, participantRole: string) {
  call.setViewport(speakerView || document.body);
  const waitingState = createWaitingState(participantRole);

  let dominantSpeakerSessionId: string | null = null;

  const updateLayout = (participants: any[]) => {
    const localUserId = call.currentUserId;
    const remoteParticipants = participants.filter((p) => p.userId !== localUserId);
    waitingState.classList.toggle("hidden", remoteParticipants.length > 0);

    let speakerParticipant = participants.find(
      (p) => p.sessionId === dominantSpeakerSessionId && p.userId !== localUserId,
    );
    if (!speakerParticipant) {
      speakerParticipant = participants.find((p) => p.userId !== localUserId);
    }

    participants.forEach((participant) => {
      const isLocalUser = participant.userId === localUserId;
      const isSpeaker = participant.sessionId === speakerParticipant?.sessionId;

      let container = document.querySelector<HTMLElement>(
        `.participant-tile[data-session-id="${participant.sessionId}"]`,
      );

      if (!container) {
        container = document.createElement("div");
        container.className = "participant-tile";
        container.dataset.sessionId = participant.sessionId;

        const nameLabel = document.createElement("div");
        nameLabel.className = "participant-name";
        nameLabel.textContent = participant.name || participant.userId || "Invitado";
        if (isLocalUser) {
          nameLabel.textContent += " (Ud)";
        }

        container.appendChild(nameLabel);
      }

      const targetParent = isSpeaker && !isLocalUser ? speakerView : sidebarParticipants;

      if (container.parentElement !== targetParent) {
        targetParent.appendChild(container);
      }

      renderParticipant(call, participant, container, speakerView);
    });

    const currentSessionIds = participants.map((p) => p.sessionId);
    document.querySelectorAll(".participant-tile").forEach((tile) => {
      const tileSessionId = (tile as HTMLElement).dataset.sessionId;
      if (tileSessionId && !currentSessionIds.includes(tileSessionId)) {
        tile.remove();
        cleanupParticipant(tileSessionId);
      }
    });
  };

  call.state.dominantSpeaker$.subscribe((dominantSpeaker) => {
    dominantSpeakerSessionId = dominantSpeaker?.sessionId || null;
    updateLayout(call.state.participants);
  });

  call.state.participants$.subscribe((participants) => {
    updateLayout(participants);
  });
}

function setupDisconnectionTimeout(
  call: Call,
  appointmentId: string,
  participantRole: string,
) {
  const timeoutSeconds = Number.isFinite(DISCONNECTION_TIMEOUT_SECONDS)
    ? Math.max(0, DISCONNECTION_TIMEOUT_SECONDS)
    : DEFAULT_DISCONNECTION_TIMEOUT_SECONDS;

  call.setDisconnectionTimeout(timeoutSeconds);
  captureMonitoringMessage("Stream disconnection timeout configured", "info", {
    appointmentId,
    participantRole,
    timeoutSeconds,
  });
}

function updateConnectionStatusBanner(state: CallingState) {
  const isReconnecting =
    state === CallingState.RECONNECTING || state === CallingState.OFFLINE;
  const hasFailed = state === CallingState.RECONNECTING_FAILED;

  connectionStatusBanner.classList.toggle(
    "connection-status-banner--failed",
    hasFailed,
  );

  if (isReconnecting) {
    connectionStatusBanner.innerHTML = `
      <div class="connection-status-title">Conexion inestable</div>
      <div class="connection-status-desc">Estamos intentando reconectar la llamada.</div>
    `;
    connectionStatusBanner.classList.remove("hidden");
    return;
  }

  if (hasFailed) {
    connectionStatusBanner.innerHTML = `
      <div>
        <div class="connection-status-title">No pudimos reconectar</div>
        <div class="connection-status-desc">Revisa tu conexion e intenta ingresar nuevamente.</div>
      </div>
      <button type="button" class="connection-status-action">Reintentar</button>
    `;
    connectionStatusBanner
      .querySelector(".connection-status-action")
      ?.addEventListener("click", () => window.location.reload());
    connectionStatusBanner.classList.remove("hidden");
    return;
  }

  connectionStatusBanner.classList.add("hidden");
}

function registerMonitoring(
  client: StreamVideoClient,
  call: Call,
  appointmentId: string,
  participantRole: string,
) {
  client.on("network.changed", (event) => {
    if (!event.online) {
      captureMonitoringMessage("Client network went offline during call", "warning", {
        appointmentId,
        participantRole,
      });
    }
  });

  client.on("connection.changed", (event) => {
    if (!event.online) {
      captureMonitoringMessage("Client connection lost during call", "warning", {
        appointmentId,
        participantRole,
      });
    }
  });

  client.on("connection.recovered", () => {
    captureMonitoringMessage("Client connection recovered", "info", {
      appointmentId,
      participantRole,
    });
  });

  call.on("call.session_ended", (event) => {
    captureMonitoringMessage("Call session ended", "warning", {
      appointmentId,
      participantRole,
      eventType: event.type,
    });
  });

  call.on("call.ended", (event) => {
    captureMonitoringMessage("Call ended", "info", {
      appointmentId,
      participantRole,
      eventType: event.type,
    });
  });

  call.on("call.session_participant_left", (event) => {
    captureMonitoringMessage("Participant left call session", "info", {
      appointmentId,
      participantRole,
      eventType: event.type,
    });
  });

  let lastReportedCallingState: CallingState | null = null;
  let interruptedAt: number | null = null;

  call.state.callingState$.subscribe((state) => {
    updateConnectionStatusBanner(state);

    const isInterrupted =
      state === CallingState.RECONNECTING || state === CallingState.OFFLINE;

    if (isInterrupted && !interruptedAt) {
      interruptedAt = Date.now();
    }

    if (state === CallingState.JOINED && interruptedAt) {
      captureMonitoringMessage("Call connection restored", "info", {
        appointmentId,
        participantRole,
        callingState: state,
        disconnectedDurationMs: Date.now() - interruptedAt,
      });
      interruptedAt = null;
    }

    if (state === lastReportedCallingState) {
      return;
    }

    lastReportedCallingState = state;

    if (isInterrupted) {
      captureMonitoringMessage("Call connection interrupted", "warning", {
        appointmentId,
        participantRole,
        callingState: state,
      });
    }

    if (state === CallingState.RECONNECTING_FAILED) {
      captureMonitoringMessage("Call reconnection failed", "error", {
        appointmentId,
        participantRole,
        callingState: state,
        disconnectedDurationMs: interruptedAt ? Date.now() - interruptedAt : null,
      });
      interruptedAt = null;
    }

    if (state === CallingState.LEFT) {
      captureMonitoringMessage("Call left", "info", {
        appointmentId,
        participantRole,
        callingState: state,
      });
    }
  });
}

init();
